import { Check, Loader2, X, Circle, AlertCircle } from 'lucide-react';
import { useState } from 'react';

export interface ProvisioningStep {
  id: string;
  label: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  error?: string;
  cause?: string;
  recovery?: string;
}

const ICON_BY_STATUS = {
  pending: Circle,
  running: Loader2,
  completed: Check,
  failed: X,
  skipped: Circle,
};

const ICON_COLORS = {
  pending: 'text-slate-400 border-slate-300 bg-white',
  running: 'text-live-sync border-live-sync/40 bg-live-sync/5',
  completed: 'text-armed-ok border-armed-ok/40 bg-armed-ok/10',
  failed: 'text-alert-breach border-alert-breach/50 bg-alert-breach/10',
  skipped: 'text-slate-400 border-slate-300 bg-white',
};

const TEXT_COLORS = {
  pending: 'text-slate-500',
  running: 'text-ink-900',
  completed: 'text-ink-900',
  failed: 'text-alert-breach',
  skipped: 'text-slate-400',
};

function formatDuration(start?: string, end?: string): string | null {
  if (!start) return null;
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const sec = Math.round((endMs - startMs) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export function StepTimeline({ steps }: { steps: ProvisioningStep[] }) {
  return (
    <ol className="relative">
      {steps.map((step, i) => (
        <StepRow key={step.id} step={step} isLast={i === steps.length - 1} />
      ))}
    </ol>
  );
}

function StepRow({ step, isLast }: { step: ProvisioningStep; isLast: boolean }) {
  const [expanded, setExpanded] = useState(step.status === 'failed');
  const Icon = ICON_BY_STATUS[step.status];
  const duration = formatDuration(step.startedAt, step.completedAt);
  const hasDetails = step.error || step.cause;

  return (
    <li className="relative pl-12 pb-6 last:pb-0">
      {!isLast && (
        <span
          className={`absolute left-[19px] top-9 bottom-0 w-px ${
            step.status === 'completed' ? 'bg-armed-ok/30' : 'bg-slate-200'
          }`}
        />
      )}
      <span
        className={`absolute left-0 top-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
          ICON_COLORS[step.status]
        }`}
      >
        <Icon size={18} className={step.status === 'running' ? 'animate-spin' : ''} />
      </span>

      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className={`font-medium ${TEXT_COLORS[step.status]}`}>{step.label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{step.description}</div>
        </div>
        {duration && (
          <div className="text-xs text-slate-400 whitespace-nowrap">
            {step.status === 'running' ? `${duration} elapsed` : duration}
          </div>
        )}
      </div>

      {step.status === 'failed' && (
        <div className="mt-3 rounded-lg border border-alert-breach/30 bg-alert-breach/5 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-alert-breach mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-alert-breach text-sm">
                {step.error ?? 'Step failed'}
              </div>
              {step.recovery && (
                <div className="text-xs text-ink-600 mt-2 leading-relaxed">
                  <span className="font-semibold uppercase tracking-wide text-alert-breach mr-1">
                    Recovery:
                  </span>
                  {step.recovery}
                </div>
              )}
              {hasDetails && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-slate-500 hover:text-ink-800 mt-2 underline-offset-2 hover:underline"
                >
                  {expanded ? 'Hide' : 'Show'} technical details
                </button>
              )}
              {expanded && step.cause && (
                <pre className="mt-2 text-[11px] text-ink-600 bg-slate-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                  {step.cause}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
